// Design Ref: §6 AlertCenter.js — 벨 + 드롭다운 최근 7일 (Session 7)
// Plan SC: FR-09 Home 알림 센터
import { useState, useEffect, useRef, useCallback } from "react";
import { Ico } from "./lib/icons";

const BASE_URL = process.env.REACT_APP_API_URL || "";

const TYPE_ICON = {
  short_surge: <Ico n="trending-down" mr={0} />,
  earnings_d3: <Ico n="calendar-days" mr={0} />,
  earnings_d1: <Ico n="calendar-days" mr={0} />,
  earnings_day: <Ico n="calendar-days" mr={0} />,
  earnings_post: <Ico n="circle-check" mr={0} />,
  halt: <Ico n="siren" mr={0} />,
  cap_top10: <Ico n="trophy" mr={0} />,
  price_alert: <Ico n="bell" mr={0} />,
};

const TYPE_LABEL = {
  short_surge: "공매도 급증",
  earnings_d3: "실적 D-3",
  earnings_d1: "실적 D-1",
  earnings_day: "실적 당일",
  earnings_post: "실적 발표",
  halt: "거래정지",
  cap_top10: "시총 Top10",
  price_alert: "목표가 알림",
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  return `${d}일 전`;
}

export default function AlertCenter({ userId }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const load = useCallback(() => {
    if (!userId) return;
    fetch(`${BASE_URL}/alerts/recent?userId=${encodeURIComponent(userId)}&days=7`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // 30초 폴링
  useEffect(() => {
    if (!userId) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [userId, load]);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const unreadCount = items.filter(i => !i.read).length;

  const markRead = async (id) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, read: true } : i));
    try { await fetch(`${BASE_URL}/alerts/read/${id}`, { method: "PATCH" }); } catch {}
  };

  const markAllRead = async () => {
    setItems(prev => prev.map(i => ({ ...i, read: true })));
    try {
      await fetch(`${BASE_URL}/alerts/read-all`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
    } catch {}
  };

  if (!userId) return null;

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        title="알림 센터"
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          padding: "6px 8px", position: "relative", color: "var(--text)",
          fontSize: 18, lineHeight: 1,
        }}
      >
        <Ico n="bell" mr={0} />
        {unreadCount > 0 && (
          <span style={{
            position: "absolute", top: 2, right: 2,
            background: "#fb7185", color: "#fff",
            fontSize: 9, fontWeight: 700,
            minWidth: 14, height: 14, borderRadius: 7,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", fontFamily: "'DM Mono', monospace",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)",
          width: 360, maxHeight: 480,
          background: "var(--bg-card, #1a1a1a)", border: "1px solid var(--border, #333)",
          borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          overflow: "hidden", zIndex: 1000,
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 14px", borderBottom: "1px solid var(--border-light)",
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
              알림 <span style={{ color: "var(--text-sub)", fontSize: 11, marginLeft: 4 }}>(최근 7일)</span>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{ background: "transparent", border: "none", color: "#60a5fa", fontSize: 11, cursor: "pointer" }}
              >
                모두 읽음
              </button>
            )}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {items.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "var(--text-muted)", fontSize: 12 }}>
                아직 알림이 없습니다
              </div>
            ) : (
              items.map(i => (
                <div
                  key={i.id}
                  onClick={() => !i.read && markRead(i.id)}
                  style={{
                    padding: "10px 14px", borderBottom: "1px solid var(--divider-row, rgba(255,255,255,0.04))",
                    cursor: i.read ? "default" : "pointer",
                    background: i.read ? "transparent" : "rgba(96,165,250,0.04)",
                    display: "grid", gridTemplateColumns: "24px 1fr auto", gap: 8, alignItems: "start",
                  }}
                >
                  <div style={{ fontSize: 14 }}>{TYPE_ICON[i.type] || <Ico n="bell" mr={0} />}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 10, color: "var(--text-sub)", fontFamily: "'DM Mono', monospace", textTransform: "uppercase" }}>
                        {TYPE_LABEL[i.type] || i.type}
                      </span>
                      {i.name && <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text)" }}>{i.name}</span>}
                      {!i.read && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#60a5fa", display: "inline-block" }} />}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-sub)", lineHeight: 1.4, whiteSpace: "pre-line" }}>
                      {(i.message || "").slice(0, 180)}
                    </div>
                  </div>
                  <div style={{ fontSize: 9, color: "var(--text-muted)", fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
                    {timeAgo(i.triggered_at)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
